import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@astro/api'
import { useAuth } from '@astro/auth'
import { useSocket, useSocketEvent } from '@astro/socket'
import { appUrl } from '@astro/config'
import PageLayout from '../components/layout/PageLayout'
import FloatingFabs from '@astro/floating-fabs'
import { useToast } from '../context/ToastContext'
import { FaArrowLeft } from 'react-icons/fa'
import {
  unwrapBookingRecord, statusFlags, scheduleFacts, paymentFacts, birthFacts,
  formatRate, isNoteFinalized, statusMeta, writeCachedAstrologerNote, uploadedPhotos,
} from '../components/sections/bookingDetails/bookingDetailsData'
import {
  DetailHero, SectionCard, FactRow, NoteDocument, DetailSkeleton, DetailError, PhotoGallery,
} from '../components/sections/bookingDetails/DetailBits'
import ReschedulePanel from '../components/sections/bookingDetails/ReschedulePanel'
import SuggestProductPanel from '../components/sections/bookingDetails/SuggestProductPanel'
import DictationButton from '../components/common/DictationButton'
import { appendSpokenText } from '../hooks/useSpeechToText'

const DASHBOARD_HREF = appUrl('website', '/dashboard?tab=consultations')

/**
 * Astrologer-side appointment detail — the host's view of one consultation,
 * rendered from GET /v1/booking-by-id/:id (same read as the seeker-side
 * BookingDetailsPage). The dark DetailHero doubles as the canvas the
 * transparent site header needs; the body spreads across a 12-col grid.
 * Status drives which actions surface; cancel, reschedule and the
 * consultation-note workflow hit the live endpoints. Starting the video room
 * lands with the upcoming video-consultation work.
 */
export default function AppointmentDetailsPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { status: authStatus } = useAuth()
  const { showToast } = useToast()
  const { emit } = useSocket()
  const [copied, setCopied] = useState(false)
  // Armed from the hero's Cancel button; the confirmation panel renders in the body.
  const [confirmingCancel, setConfirmingCancel] = useState(false)

  useEffect(() => {
    if (authStatus === 'unauthenticated') {
      navigate(`/login?redirect=${encodeURIComponent(`/astrologer/appointments/${id}`)}`)
    }
  }, [authStatus, id, navigate])

  const queryClient = useQueryClient()
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['booking-details', id],
    queryFn: () => api.bookings.byId(id),
    enabled: Boolean(id) && authStatus === 'authenticated',
    retry: false,
  })

  const booking = data?.status === false ? null : unwrapBookingRecord(data)
  const bookingCode = `BKNG${id}`
  const loading = isPending || authStatus === 'loading'

  const flags = statusFlags(booking?.status)
  const alreadyLive = ['ready_to_start', 'in_progress'].includes(String(booking?.status || '').toLowerCase())

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['booking-details', id] })
  const apiMessage = (err, fallback) => err?.error?.message || err?.message || fallback

  /**
   * Start Video Consultation:
   *  1. PATCH /booking-by-id/:id → { meeting_id: "astro-{id}", status:
   *     "ready_to_start" } — the exact payload the backend expects (verified
   *     against the legacy portal's AstrologerApiService)
   *  2. ring the seeker over the socket (consultation:invite)
   *  3. head into the room and wait — the room itself is resolved inside
   *     VideoConsultationPage from the booking id (customRoomId astro-{id}).
   */
  const startVideoMutation = useMutation({
    mutationFn: async () => {
      // Every press rings the seeker's phone as a new call — including when
      // the booking already reads live (they declined or missed the last one).
      // Skipping it then is what made "call again" silent on the app.
      try {
        await api.bookings.ringCall(booking.id)
      } catch (err) {
        // ApiRequestError nests the HTTP status under `error`. 404 = an API
        // without /call/ring yet: fall back to the old status PATCH.
        const httpStatus = err?.error?.status ?? err?.status
        if (httpStatus !== 404) throw err
        if (!alreadyLive) await api.bookings.startVideo(booking.id)
      }
    },
    onSuccess: () => {
      // emit() returns false while the socket is down — the room re-rings on
      // reconnect, but surface it so a lost first ring isn't a mystery.
      const sent = emit('consultation:invite', { toUserId: booking.user_id, bookingId: booking.id })
      if (!sent) showToast('Realtime connection is offline — the customer will be rung once it reconnects.')
      refresh()
      navigate(`/consultations/${booking.id}/video`)
    },
    onError: (err) => showToast(apiMessage(err, 'Could not start the video consultation.')),
  })

  // Seeker's live responses to the invite (while we're still on this page).
  useSocketEvent('consultation:accept', (payload) => {
    if (booking && String(payload?.bookingId) === String(booking.id)) showToast('Customer is joining the consultation.')
  })
  useSocketEvent('consultation:decline', (payload) => {
    if (booking && String(payload?.bookingId) === String(booking.id)) showToast('Customer declined the video call.')
  })

  // The upstream cancel is a status PATCH on booking-by-id (verified against
  // the legacy portal) — there is no separate cancel route or reason field.
  const cancelMutation = useMutation({
    mutationFn: () => api.bookings.cancelViaStatus(booking.id),
    onSuccess: () => {
      setConfirmingCancel(false)
      refresh()
      showToast('Appointment cancelled.')
    },
    onError: (err) => showToast(apiMessage(err, 'Could not cancel this appointment.')),
  })

  const schedule = booking ? scheduleFacts(booking) : null
  const heroFacts = booking
    ? [
      {
        label: 'Consultation',
        value: `${booking.category === 'gemstone' ? 'Gemstone · ' : ''}${(booking.consultation_type || 'Consultation').replace(/^\w/, (c) => c.toUpperCase())}`,
      },
      { label: 'Date', value: schedule.dateLabel },
      { label: 'Slot', value: schedule.slotLabel },
      { label: 'Duration', value: schedule.durationLabel },
      { label: 'Rate', value: formatRate(booking.rate) },
      { label: 'Status', value: <span className={statusMeta(booking.status).heroCls}>{statusMeta(booking.status).label}</span> },
    ]
    : []

  const copyBookingId = () => {
    navigator.clipboard?.writeText(bookingCode).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
    })
  }

  return (
    <PageLayout className="bg-[#403c3d]/5 text-[#151b21] antialiased min-h-screen flex flex-col">
      <main className="grow">
        <DetailHero
          backHref={DASHBOARD_HREF}
          backLabel="My Appointments"
          title="Appointment Details"
          code={bookingCode}
          facts={heroFacts}
          action={
            /* Stacked action block — the primary call sits on its own row,
               the two quieter actions pair up beneath it at equal weight. */
            <div className="flex flex-col items-stretch gap-2.5">
              {booking && flags.canStartVideo && (
                <button
                  type="button"
                  disabled={startVideoMutation.isPending}
                  onClick={() => startVideoMutation.mutate()}
                  className="hero-btn-primary inline-flex items-center justify-center gap-2 rounded-full px-7 py-3 text-[10px] font-bold uppercase tracking-[0.2em] disabled:opacity-60"
                >
                  {startVideoMutation.isPending
                    ? '📹 Opening Room…'
                    : alreadyLive
                      ? '📹 Enter Consultation Room'
                      : '📹 Start Video Consultation'}
                </button>
              )}
              <div className="flex flex-wrap items-center justify-end gap-2.5">
                {/* Straight back to the dashboard's consultations tab — the
                    landing spot after a session ends. */}
                <a
                  href={DASHBOARD_HREF}
                  className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.18em] border border-secondary/40 text-secondary hover:bg-secondary/10 transition-colors"
                >
                  <FaArrowLeft className="text-xs" />
                  My Appointments
                </a>

                {booking && (
                  <button
                    type="button"
                    onClick={() => setConfirmingCancel(true)}
                    disabled={!flags.canCancel || confirmingCancel}
                    className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.18em] border border-red-400/50 text-red-300 hover:bg-red-400/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    title={flags.canCancel ? 'Cancel this appointment' : 'This appointment can no longer be cancelled.'}
                  >
                    ✕ Cancel Appointment
                  </button>
                )}
              </div>
            </div>
          }
          onCopy={booking ? copyBookingId : undefined}
          copied={copied}
        />
        {loading ? (
          <DetailSkeleton />
        ) : isError || !booking ? (
          <DetailError id={id} onRetry={refetch} backHref={DASHBOARD_HREF} backLabel="My Appointments" />
        ) : (
          <AppointmentBody
            booking={booking}
            queryId={id}
            confirmingCancel={confirmingCancel}
            setConfirmingCancel={setConfirmingCancel}
            cancelMutation={cancelMutation}
          />
        )}
      </main>
      <FloatingFabs />
    </PageLayout>
  )
}

/** Mounted only once the booking exists, so local state can seed from it.
    The Start Video / Cancel actions live in the parent's hero; only the
    inline cancel confirmation renders here. */
function AppointmentBody({ booking, queryId, confirmingCancel, setConfirmingCancel, cancelMutation }) {
  const { showToast } = useToast()
  const queryClient = useQueryClient()

  const flags = statusFlags(booking.status)
  const payment = paymentFacts(booking)
  const birth = birthFacts(booking)
  const noteFinalized = isNoteFinalized(booking)
  const photos = uploadedPhotos(booking)

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['booking-details', queryId] })

  // Set by the Suggest Products panel; called once, from Confirm Finalize, to
  // publish everything the astrologer staged here and inside the video room.
  const suggestionsCommitRef = useRef(null)

  const actionBtn ='inline-flex items-center gap-2 rounded-full px-6 py-3 text-[10px] font-bold uppercase tracking-[0.2em] border transition-colors disabled:opacity-40 disabled:cursor-not-allowed'

  return (
    <div className="container px-6 mx-auto pt-8 pb-24">
      {/* Status banners */}
      {flags.isCancelled && (
        <div className="rounded-2xl border border-red-200 bg-red-50/60 px-6 py-4 text-sm text-red-600 mb-6">
          This appointment has been cancelled — all actions are disabled.
        </div>
      )}
      {flags.isCompleted && (
        <div className="rounded-2xl border border-blue-200 bg-blue-50/60 px-6 py-4 text-sm text-blue-700 mb-6">
          This appointment is completed. Video consultation is no longer available.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* ── Main column ── */}
        <div className="lg:col-span-8 space-y-6">
          {/* Inline cancel confirmation — armed from the hero's Cancel button;
              cancelling affects both parties. */}
          {confirmingCancel && (
            <div className="rounded-2xl border border-red-200 bg-red-50/40 p-5 space-y-3">
              <p className="text-sm font-semibold text-red-600">This will cancel the appointment for both you and the customer. Continue?</p>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  disabled={cancelMutation.isPending}
                  onClick={() => cancelMutation.mutate()}
                  className={`${actionBtn} border-red-400 bg-red-500 text-white hover:bg-red-600`}
                >
                  {cancelMutation.isPending ? 'Cancelling…' : 'Confirm Cancel'}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingCancel(false)}
                  className={`${actionBtn} border-[#C8922A]/30 text-[#8B7355]`}
                >
                  Keep Appointment
                </button>
              </div>
            </div>
          )}

          {/* Customer facts — contact + the birth details charted from. */}
          <SectionCard title="Customer Details" icon="🙏">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-1.5">
              <FactRow label="Name" value={booking.name || '—'} />
              <FactRow label="Birth Date" value={birth.date || '—'} />
              <FactRow label="Email" value={booking.email || '—'} />
              <FactRow label="Birth Time" value={birth.time || '—'} />
              <FactRow label="Mobile" value={booking.phone || '—'} />
              <FactRow label="Birth Place" value={birth.place || '—'} />
            </div>
            {booking.notes && (
              <div className="mt-5 pt-4 border-t border-[#C8922A]/15">
                <p className="text-[10px] font-bold uppercase tracking-widest text-[#8B7355] mb-1.5">Booking Notes</p>
                <p className="text-sm text-[#403c3d] whitespace-pre-line">{booking.notes}</p>
              </div>
            )}
          </SectionCard>

          {/* Face/palm photos the customer submitted in place of an exact birth
              time (Maharaj flow only) — only shown when at least one is present. */}
          {photos.length > 0 && (
            <SectionCard title="Uploaded Photos" icon="🖼️">
              <PhotoGallery photos={photos} />
            </SectionCard>
          )}

          {/* Product suggestions — the upstream refuses changes once the
              appointment is cancelled or the note is finalized, so lock the
              panel under the same conditions instead of surfacing 422s.
              Picks are staged until Confirm Finalize flushes them. */}
          <SuggestProductPanel
            booking={booking}
            commitRef={suggestionsCommitRef}
            disabled={flags.isCancelled || noteFinalized}
            lockedReason={
              flags.isCancelled
                ? 'This appointment was cancelled — suggestions are disabled.'
                : 'This appointment has been finalized — suggestions can no longer be changed.'
            }
          />

          {/* Consultation note workflow */}
          <SectionCard title="Astrologer Note" icon="📜">
            {noteFinalized ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-2.5">
                  🔒 This note has been finalized and shared with the customer.
                </div>
                <NoteDocument booking={booking} />
              </div>
            ) : (
              <NoteEditor
                booking={booking}
                disabled={flags.isCancelled}
                onSaved={refresh}
                suggestionsCommitRef={suggestionsCommitRef}
              />
            )}
          </SectionCard>
        </div>

        {/* ── Sidebar ── */}
        <aside className="lg:col-span-4 space-y-6 lg:sticky lg:top-40">
          <SectionCard>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#8B7355]">Total Amount</p>
            <p className="font-cinzel text-4xl text-[#1C140A] mt-3">{formatRate(booking.rate)}</p>
            <p className="text-xs text-[#8B7355] mt-2">Consultation payment has been received and recorded.</p>
          </SectionCard>

          <SectionCard title="Payment Info" icon="💳">
            <div className="space-y-1.5">
              <FactRow label="Method" value={payment.methodLabel} />
              <FactRow
                label="Transaction ID"
                value={payment.transactionId ? <span className="font-mono text-xs">{payment.transactionId}</span> : '—'}
              />
              <FactRow label="Status" value={<span className="text-green-600">Paid</span>} />
            </div>
          </SectionCard>

          {/* Reschedule — locked once the session is ready/live/done */}
          {flags.canReschedule && (
            <ReschedulePanel
              booking={booking}
              onDone={() => showToast('Appointment rescheduled.')}
              onError={(message) => showToast(message)}
            />
          )}
        </aside>
      </div>
    </div>
  )
}

/**
 * Draft → finalize note workflow. Save Draft PATCHes /bookings/:id/
 * astrologer-note; Close & Finalize saves, then PATCHes the note status to
 * "finalized" — which locks it here and publishes it on the seeker's page.
 * Finalizing is also what publishes the staged product suggestions, so the
 * customer receives the note and the remedies in the same moment.
 */
function NoteEditor({ booking, disabled, onSaved, suggestionsCommitRef }) {
  const { showToast } = useToast()
  const queryClient = useQueryClient()
  const [note, setNote] = useState(booking.astrologer_note ?? '')
  const [dirty, setDirty] = useState(false)
  const [confirmingFinalize, setConfirmingFinalize] = useState(false)

  const noteRef = useRef(note)
  noteRef.current = note
  const savedRef = useRef(booking.astrologer_note ?? '')
  const autosaveTimer = useRef(null)

  // The in-call notes card saves the same draft — when a background refetch
  // brings a newer note and nothing has been typed here yet, adopt it, so
  // notes written during the stream carry forward into this editor.
  useEffect(() => {
    if (!dirty) {
      setNote(booking.astrologer_note ?? '')
      savedRef.current = booking.astrologer_note ?? ''
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking.astrologer_note])

  // Auto-run the same save as the Save Draft button: persist the note (and
  // mirror it into the shared booking-details cache) shortly after typing
  // stops, and flush on the way out. That's what carries an unsaved note into
  // the in-call editor — no explicit click needed. Errors stay silent here;
  // the manual Save Draft surfaces them.
  const autosave = async () => {
    const value = noteRef.current
    if (disabled || value === savedRef.current || !value.trim()) return
    try {
      await api.bookings.updateAstrologerNote(booking.id, value)
      savedRef.current = value
      writeCachedAstrologerNote(queryClient, booking.id, value)
    } catch { /* best-effort autosave */ }
  }
  useEffect(() => {
    clearTimeout(autosaveTimer.current)
    autosaveTimer.current = setTimeout(autosave, 1000)
    return () => clearTimeout(autosaveTimer.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [note])
  useEffect(() => () => { autosave() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const apiMessage = (err, fallback) => err?.error?.message || err?.message || fallback

  const saveMutation = useMutation({
    mutationFn: () => api.bookings.updateAstrologerNote(booking.id, note),
    onSuccess: () => {
      savedRef.current = noteRef.current // keep autosave from re-saving the same text
      showToast('Note draft saved.')
      onSaved?.()
    },
    onError: (err) => showToast(apiMessage(err, 'Could not save the note.')),
  })

  const finalizeMutation = useMutation({
    mutationFn: async () => {
      await api.bookings.updateAstrologerNote(booking.id, note)
      // Products picked here and during the video call were held back so the
      // customer's cart wouldn't change under them mid-consultation — this is
      // the moment they go out. It has to happen BEFORE the note is finalized:
      // upstream refuses suggestion writes once the booking is locked, so a
      // flush afterwards would 422 and strand the picks forever. If any row
      // fails, abort rather than lock the appointment around lost suggestions.
      const flushed = await suggestionsCommitRef?.current?.()
      if (flushed?.failed) {
        throw new Error(
          `${flushed.failed} product suggestion${flushed.failed === 1 ? '' : 's'} could not be shared with the customer — the appointment was not finalized. Please try again.`,
        )
      }
      await api.bookings.finalizeAstrologerNote(booking.id)
      // Finalizing is what closes the session: ending the call does NOT mark
      // the booking completed — this does (meeting_ended_at + completed).
      const status = String(booking.status || '').toLowerCase()
      if (!['completed', 'cancelled'].includes(status)) {
        await api.bookings.endVideo(booking.id)
      }
      return flushed
    },
    onSuccess: (flushed) => {
      setConfirmingFinalize(false)
      const shared = flushed?.added
        ? ` ${flushed.added} product suggestion${flushed.added === 1 ? '' : 's'} shared with the customer.`
        : ''
      showToast('Session finalized and marked complete.' + shared)
      onSaved?.()
      // Land the astrologer on the dashboard's consultations tab.
      setTimeout(() => window.location.assign(DASHBOARD_HREF), 900)
    },
    onError: (err) => showToast(apiMessage(err, 'Could not finalize the notes.')),
  })

  const busy = saveMutation.isPending || finalizeMutation.isPending
  // The backend refuses to save/finalize an empty note ('required' rule +
  // finalize guard) — surface that up front instead of as an API error.
  const noteEmpty = !note.trim();

  return (
    <div className="space-y-4">
      {/* Voice typing — dictated phrases append to the draft (autosave/finalize
          treat them exactly like typed text). Hidden where the browser can't
          transcribe, and locked while a save/finalize is mid-flight. */}
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-[#8B7355]">🎙 Voice typing</span>
        <DictationButton
          onTranscript={(text) => { setDirty(true); setNote((n) => appendSpokenText(n, text)) }}
          disabled={disabled || busy}
        />
      </div>
      <textarea
        value={note}
        onChange={(e) => { setDirty(true); setNote(e.target.value) }}
        disabled={disabled || busy}
        rows={7}
        placeholder="Write consultation notes for this appointment — the customer sees them once finalized…"
        className="w-full rounded-xl border border-[#C8922A]/20 bg-[#FDFBF7] px-4 py-3.5 text-sm leading-6 text-[#1C140A] resize-y focus:outline-none focus:border-[#C8922A] disabled:opacity-50"
      />
      {confirmingFinalize ? (
        <div className="rounded-xl border border-green-200 bg-green-50/60 p-4 space-y-3">
          <p className="text-sm text-green-800">
            Finalizing locks this note, shares it with the customer, and marks the appointment as completed. Continue?
          </p>
          <div className="flex flex-wrap items-center justify-end gap-3">
            <button
              type="button"
              disabled={busy}
              onClick={() => finalizeMutation.mutate()}
              className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-[10px] font-bold uppercase tracking-[0.2em] bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {finalizeMutation.isPending ? 'Finalizing…' : 'Confirm Finalize'}
            </button>
            <button
              type="button"
              onClick={() => setConfirmingFinalize(false)}
              className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-[10px] font-bold uppercase tracking-[0.2em] border border-[#C8922A]/30 text-[#8B7355] transition-colors"
            >
              Keep Editing
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-end gap-3">
          {noteEmpty && (
            <p className="text-[11px] text-[#8B7355] mr-auto">
              Write the consultation note to save or finalize it.
            </p>
          )}
          <button
            type="button"
            disabled={disabled || busy || noteEmpty}
            onClick={() => saveMutation.mutate()}
            title={noteEmpty ? 'The note cannot be empty.' : undefined}
            className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-[10px] font-bold uppercase tracking-[0.2em] border border-[#C8922A]/40 text-[#C8922A] hover:bg-[#C8922A]/10 transition-colors disabled:opacity-40"
          >
            {saveMutation.isPending ? 'Saving…' : '💾 Save Draft'}
          </button>
          <button
            type="button"
            disabled={disabled || busy || noteEmpty}
            onClick={() => setConfirmingFinalize(true)}
            title={noteEmpty ? 'Write a consultation note before finalizing.' : undefined}
            className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-[10px] font-bold uppercase tracking-[0.2em] border border-green-500 text-green-600 hover:bg-green-50 transition-colors disabled:opacity-40"
          >
            ✓ Close &amp; Finalize
          </button>
        </div>
      )}
    </div>
  )
}
